import React, { FC, useEffect, useRef } from 'react'
import { ResidentAppNotification } from '@pandiafi/components'
import { useTranslation } from 'react-i18next'
import { useLocation } from 'react-router-dom'

import { UserMetadata, UserNotification } from '../../../common/validations'
import {
  ContractType,
  ResidentNotificationCategory,
  ResidentNotificationPriority,
} from '../../../common/types'
import { ShownCategory } from './utils'

import { useAPICloseUserNotification } from 'api/apiHooks/useAPICloseUserNotification'
import { params as useAPIGetUserNotificationsParams } from 'api/apiHooks/useAPIGetUserNotifications'
import { queryClient } from 'reactQuery'
import { useContractPaths } from 'hooks/useContractPaths/useContractPaths'

const ResidentNotification: FC<{
  notification: UserNotification
  userMetadata: UserMetadata
  contractType?: ContractType
}> = ({ notification, userMetadata, contractType }) => {
  const { t } = useTranslation(['notifications'])
  const {
    mutate: handleClose,
    isPending,
    isSuccess,
  } = useAPICloseUserNotification(notification.id, {
    onSuccess: () => {
      queryClient.invalidateQueries({
        predicate: (query) =>
          // eslint-disable-next-line no-object-comparison/loose-type
          query.queryKey[0] === useAPIGetUserNotificationsParams.queryKey[0],
      })
    },
    onError: () => {
      // Silently handle expected errors when notification is already closed
    },
  })
  const location = useLocation()
  const contractTerminationApprovalTitleKey =
    contractType === ContractType.RIGHT_OF_OCCUPANCY
      ? 'notifications:contract_termination_request_approval_needed.rightOfOccupancyNoticeTitle'
      : 'notifications:contract_termination_request_approval_needed.title'

  const closeAttemptedRef = useRef(false)
  const {
    confirmContractTerminationPath,
    messagesPath,
    confirmContractModificationPath,
  } = useContractPaths()

  useEffect(() => {
    const isOnMessagesPage = location.pathname.includes('/viestit')

    if (
      notification.category === ResidentNotificationCategory.message &&
      !notification.closedAt &&
      notification.isCloseable &&
      isOnMessagesPage &&
      !isPending &&
      !isSuccess &&
      !closeAttemptedRef.current
    ) {
      closeAttemptedRef.current = true
      handleClose()
    }
  }, [
    notification,
    location.pathname,
    messagesPath,
    handleClose,
    isPending,
    isSuccess,
  ])

  const variant =
    notification.priority === ResidentNotificationPriority.important
      ? 'danger'
      : 'neutral'
  const canClose = !notification.closedAt && !userMetadata.isImpersonator
  const onRemove = canClose && !isPending ? () => handleClose() : undefined

  // Filtered with isNotificationShown before rendering
  const category = notification.category as ShownCategory

  switch (category) {
    case ResidentNotificationCategory.arrear:
      return (
        <ResidentAppNotification
          variant="danger"
          layout="attention"
          title={t('notifications:arrear')}
        >
          -{notification.notificationMeta?.arrearAmount.replace('.', ',')} €
        </ResidentAppNotification>
      )
    case ResidentNotificationCategory.contract_termination_request_approval_needed:
      return (
        <ResidentAppNotification
          variant={variant}
          title={t(contractTerminationApprovalTitleKey)}
          href={confirmContractTerminationPath}
        >
          {t(
            'notifications:contract_termination_request_approval_needed.content'
          )}
        </ResidentAppNotification>
      )
    case ResidentNotificationCategory.contract_modification_request_approval_needed:
      return (
        <ResidentAppNotification
          variant={variant}
          title={t(
            'notifications:contract_modification_request_approval_needed.title'
          )}
          href={confirmContractModificationPath}
        >
          {t(
            'notifications:contract_modification_request_approval_needed.content'
          )}
        </ResidentAppNotification>
      )
    case ResidentNotificationCategory.contract_termination_request_tenant_rejected:
      return (
        <ResidentAppNotification variant={variant} onRemove={onRemove}>
          {t('notifications:contract_termination_request_tenant_rejected')}
        </ResidentAppNotification>
      )
    case ResidentNotificationCategory.contract_termination_request_kenno_rejected:
      return (
        <ResidentAppNotification variant={variant} onRemove={onRemove}>
          {t('notifications:contract_termination_request_kenno_rejected')}
        </ResidentAppNotification>
      )
    case ResidentNotificationCategory.message:
      return (
        <ResidentAppNotification
          variant={variant}
          title={t('notifications:message')}
          href={messagesPath}
          onClick={() => {
            if (canClose) handleClose()
          }}
        >
          {t('notifications:viewMessages')}
        </ResidentAppNotification>
      )
    default: {
      // A category in shownCategories without a case here fails the build
      const unhandled: never = category
      return unhandled
    }
  }
}

export default ResidentNotification
